'use client';

import Link from 'next/link';
import { usePriceConvert } from '@/lib/PriceContext';
import type { RelatedProduct } from '@/types/pdp';

const PLATFORM_MAP: Record<string, { label: string; cls: string }> = {
  XBOXSERIESX: { label: 'X|S',  cls: 'ptag-xsx' },
  XBOXONE:     { label: 'ONE',  cls: 'ptag-xone' },
  PC:          { label: 'PC',   cls: 'ptag-pc' },
};

function fmtPrice(p: number | null, c: string | null): string {
  if (!p || !c) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: c,
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(p);
}

interface Props {
  item: RelatedProduct;
  lang: string;
  currentId?: string;
}

export function RelatedCard({ item, lang, currentId }: Props) {
  const { convert: fmtConverted } = usePriceConvert();
  const title = lang === 'ru' ? (item.title_ru || item.title_en)
    : lang === 'uk' ? ((item as any).title_uk || item.title_en)
    : item.title_en;
  const isCurrent = currentId === item.related_product_id;
  const isGame = item.product_type === 'game';
  const disc = item.discount_percent != null
    ? Math.round(Math.abs(item.discount_percent))
    : (item.msrp && item.msrp > (item.list_price ?? 0)
      ? Math.round(100 - ((item.list_price ?? 0) / item.msrp) * 100)
      : null);
  const platforms = (item.platforms ?? [])
    .map(p => PLATFORM_MAP[p])
    .filter(Boolean) as { label: string; cls: string }[];

  return (
    <Link
      href={`/product/${item.related_product_id}`}
      className="cat-card"
      style={{
        textDecoration: 'none', flexShrink: 0, width: 175, scrollSnapAlign: 'start',
        ...(isCurrent ? { borderColor: 'rgba(82,176,67,.5)', background: 'rgba(82,176,67,.06)' } : {}),
      }}
    >
      <div className="cat-art-wrap">
        <div className="cat-art-bg">
          {item.thumb_url
            ? <img src={item.thumb_url} alt={title} loading="lazy" />
            : <div style={{ width: '100%', height: '100%', background: 'linear-gradient(145deg,#1a1a2e,#16213e,#0f3460)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,.2)', textAlign: 'center', padding: 10 }}>{title}</span>
              </div>
          }
        </div>

        {/* Type badge */}
        <span className={`badge-pill badge-type ${isGame ? 'badge-game' : 'badge-dlc'}`}>
          {isGame ? 'GAME' : 'DLC'}
        </span>

        {/* Discount badge */}
        {!isCurrent && disc != null && disc > 0 && (
          <span className="badge-pill badge-disc">−{disc}%</span>
        )}

        {/* Platform tags */}
        {platforms.length > 0 && (
          <div className="cat-platforms">
            {platforms.map(p => (
              <span key={p.label} className={`platform-tag ${p.cls}`}>{p.label}</span>
            ))}
          </div>
        )}

        {/* "Це видання" banner */}
        {isCurrent && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            fontSize: 8, fontWeight: 800, letterSpacing: '.07em',
            padding: '5px 6px', textAlign: 'center',
            background: 'rgba(82,176,67,.88)', color: '#fff',
          }}>
            ЦЕ ВИДАННЯ
          </div>
        )}
      </div>

      <div className="cat-info">
        <div className="cat-name" title={title}>{title}</div>
        <div className="cat-price-row">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            {item.list_price && item.currency_code ? (
              <>
                <span className="cat-price">{fmtConverted(item.min_key_usd ?? item.list_price) ?? fmtPrice(item.min_key_usd ?? item.list_price, item.currency_code)}</span>
                {disc != null && disc > 0 && item.msrp != null && (
                  <span className="cat-price-orig">{fmtConverted(item.msrp) ?? fmtPrice(item.msrp, item.currency_code)}</span>
                )}
              </>
            ) : item.is_unreleased ? (
              <span style={{ fontSize: 10, fontWeight: 700, color: '#a78bfa', letterSpacing: '.02em' }}>
                {lang === 'ru' ? 'Скоро' : lang === 'en' ? 'Coming soon' : 'Незабаром'}
              </span>
            ) : item.product_type === 'dlc' || item.is_purchasable === false ? (
              <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--v4-fg-3)', letterSpacing: '-.01em' }}>Недоступно</span>
            ) : (
              <span style={{ fontSize: 10, fontWeight: 700, color: '#52b043', letterSpacing: '.02em' }}>Free to Play</span>
            )}
          </div>
          {item.rating != null && item.rating > 0 && (
            <div className="cat-rating">
              <span style={{ color: '#f59e0b' }}>★</span>
              <span>{item.rating.toFixed(1)}</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
